import type { PipelineSummaryItem } from "../../pages/pipeline/pipelineSummaryCondensed";
import { getChartSliceColor } from "../../utils/distinctColors";
import {
  COLORS,
  FONTS,
  formatGbp,
  formatPercent,
  hexNoHash,
} from "./pptxCore";

export type SummaryDisplayRow = {
  name: string;
  deal_count: number;
  loan_amount_gbp: number;
  average_deal_size_gbp: number;
  percentOfTotal: number;
  isTotal?: boolean;
};

export function buildSummaryDisplayRows(
  items: PipelineSummaryItem[],
  total: { deal_count: number; loan_amount_gbp: number; average_deal_size_gbp?: number },
): SummaryDisplayRow[] {
  const totalAmount = total.loan_amount_gbp || 0;
  const rows: SummaryDisplayRow[] = items.map((item) => ({
    name: item.name,
    deal_count: item.deal_count,
    loan_amount_gbp: item.loan_amount_gbp,
    average_deal_size_gbp:
      item.average_deal_size_gbp ??
      (item.deal_count > 0 ? item.loan_amount_gbp / item.deal_count : 0),
    percentOfTotal:
      totalAmount > 0 ? (item.loan_amount_gbp / totalAmount) * 100 : 0,
  }));

  rows.push({
    name: "Total",
    deal_count: total.deal_count,
    loan_amount_gbp: total.loan_amount_gbp,
    // Fallback totals (built client-side when the API omits `total`) and API
    // totals without an average are derived the same way as item rows.
    average_deal_size_gbp:
      total.average_deal_size_gbp ??
      (total.deal_count > 0 ? total.loan_amount_gbp / total.deal_count : 0),
    percentOfTotal: 100,
    isTotal: true,
  });

  return rows;
}

export function chartColorsForCount(count: number): string[] {
  return Array.from({ length: count }, (_, i) => hexNoHash(getChartSliceColor(i)));
}

export function topThreeSectorInsight(items: PipelineSummaryItem[]): string {
  const sorted = [...items].sort(
    (a, b) => b.loan_amount_gbp - a.loan_amount_gbp,
  );
  const top3 = sorted.slice(0, 3);
  if (top3.length === 0) {
    return "No sector volume available.";
  }
  const totalAmount = items.reduce((sum, item) => sum + item.loan_amount_gbp, 0);
  const topAmount = top3.reduce((sum, item) => sum + item.loan_amount_gbp, 0);
  const pct = totalAmount > 0 ? (topAmount / totalAmount) * 100 : 0;
  const names = top3.map((item) => item.name);
  const nameList =
    names.length === 1
      ? names[0]
      : names.length === 2
        ? `${names[0]} and ${names[1]}`
        : `${names[0]}, ${names[1]} and ${names[2]}`;
  return `Top three sectors of ${nameList} account for ~${Math.round(pct)}% of loan requests for deals that are not killed.`;
}

export function summaryTableCell(
  text: string,
  options?: {
    bold?: boolean;
    align?: "left" | "center" | "right";
    fill?: string;
  },
) {
  return {
    text,
    options: {
      fontFace: FONTS.body,
      fontSize: 8,
      color: COLORS.textDark,
      bold: options?.bold ?? false,
      align: options?.align ?? "left",
      valign: "middle" as const,
      fill: options?.fill ? { color: options.fill } : undefined,
    },
  };
}

export function buildSummaryTableRows(rows: SummaryDisplayRow[]) {
  const header = [
    summaryTableCell("Name", { bold: true, fill: COLORS.white }),
    summaryTableCell("Deals", {
      bold: true,
      align: "right",
      fill: COLORS.white,
    }),
    summaryTableCell("Amount", {
      bold: true,
      align: "right",
      fill: COLORS.white,
    }),
    summaryTableCell("Avg Deal Size", {
      bold: true,
      align: "right",
      fill: COLORS.white,
    }),
    summaryTableCell("% of Total", {
      bold: true,
      align: "right",
      fill: COLORS.white,
    }),
  ];

  const body = rows.map((row) => {
    const fill = row.isTotal ? COLORS.totalRow : COLORS.white;
    return [
      summaryTableCell(row.name, { bold: row.isTotal, fill }),
      summaryTableCell(row.deal_count.toLocaleString("en-GB"), {
        bold: row.isTotal,
        align: "right",
        fill,
      }),
      summaryTableCell(formatGbp(row.loan_amount_gbp), {
        bold: row.isTotal,
        align: "right",
        fill,
      }),
      summaryTableCell(formatGbp(row.average_deal_size_gbp), {
        bold: row.isTotal,
        align: "right",
        fill,
      }),
      summaryTableCell(formatPercent(row.percentOfTotal), {
        bold: row.isTotal,
        align: "right",
        fill,
      }),
    ];
  });

  return [header, ...body];
}
